// Dropping a folder of images into the assistant's brain.
//
// ─── WHAT THIS REPLACES ─────────────────────────────────────────────────────
//
// One `<input type=file>`, one image, one title, one description, one press of
// Téléverser. Adding twelve reference photos meant repeating that twelve times,
// and the screenshot that prompted this shows the failure mode: two files named
// `728e0f36-c098-…JPG` and `60643929-2eac-…JPG`, titled "Tutorial Document",
// described once. Nothing about that entry tells the assistant when to send it.
//
// ─── THE CONSTRAINT THAT SHAPES EVERY DECISION HERE ─────────────────────────
//
// ⚠️ THE ASSISTANT NEVER SEES THE IMAGE. It reads the description, as the
// panel's own first line says. So bulk upload is not "the same form, times
// twelve" — the description is the ONLY thing that makes an image findable, and
// twelve images sharing one box would be twelve entries the assistant cannot
// tell apart. Every file therefore gets its OWN description field, sitting next
// to its own thumbnail, and the queue counts how many still lack one.
//
// ⚠️ IT WARNS, IT DOES NOT BLOCK. The admin may legitimately want the pictures
// in now and the words tonight, and a form that demands twelve descriptions
// before it will upload anything is a form people stop opening. See
// `imageQueue.ts` for that rule and the counting behind it.
//
// Guard: src/pages/admin/assistant/imageQueue.test.ts (all logic lives there,
// so the queue's rules are testable without a browser).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { whatsappApi, uploadToSignedUrl } from '@/lib/api';
import { useT } from '@/lib/i18n';
import {
  partitionDropped, titleFromFilename, isMeaninglessTitle, isUsableByAssistant,
  summarise, queueMessage, ACCEPTED_TYPES, type QueuedImage, type RejectedFile,
} from './imageQueue';

const GOLD = '#C9A24A';

/** Uploads in flight at once. Sequential is needlessly slow for twelve small
 *  files; unbounded opens twelve sockets and makes every one of them crawl. */
const CONCURRENCY = 3;

let seq = 0;
const nextId = () => `q${(seq += 1)}-${Math.random().toString(36).slice(2, 8)}`;

export function BulkImageDrop({ onUploaded }: { onUploaded: () => void }) {
  const { t } = useT();
  const [items, setItems] = useState<QueuedImage[]>([]);
  const [rejected, setRejected] = useState<RejectedFile[]>([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // A counter, not a boolean: dragenter/dragleave fire for every child element
  // the cursor crosses, so a boolean flickers the highlight off mid-drag.
  const dragDepth = useRef(0);

  const summary = useMemo(() => summarise(items), [items]);

  // ⚠️ REVOKE ON UNMOUNT. Each thumbnail holds an object URL for the whole
  // file; leaving thirty of them alive holds thirty images in memory after the
  // panel closes.
  useEffect(() => () => { items.forEach((i) => URL.revokeObjectURL(i.previewUrl)); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  const addFiles = useCallback((files: File[]) => {
    setItems((prev) => {
      const { accepted, rejected: bad } = partitionDropped(files, prev.length);
      setRejected(bad);
      const added = accepted.map<QueuedImage>((f) => ({
        id: nextId(),
        file: f,
        previewUrl: URL.createObjectURL(f),
        title: titleFromFilename(f.name),
        description: '',
        status: 'ready',
      }));
      return [...prev, ...added];
    });
  }, []);

  const patch = (id: string, next: Partial<QueuedImage>) =>
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...next } : i)));

  const remove = (id: string) => setItems((prev) => {
    const gone = prev.find((i) => i.id === id);
    if (gone) URL.revokeObjectURL(gone.previewUrl);
    return prev.filter((i) => i.id !== id);
  });

  async function uploadOne(it: QueuedImage) {
    patch(it.id, { status: 'uploading', error: undefined });
    try {
      const { upload_url, storage_path } = await whatsappApi.knowledgeUploadUrl(it.file.name);
      await uploadToSignedUrl(upload_url, it.file);
      await whatsappApi.addKnowledge({
        kind: 'image',
        title: it.title.trim() || titleFromFilename(it.file.name),
        body: it.description.trim(),
        storage_path,
      });
      patch(it.id, { status: 'done' });
    } catch (e) {
      // ⚠️ THE ROW STAYS, HOLDING ITS DESCRIPTION. Clearing a failed row would
      // throw away typing the admin cannot get back, and "retry" would mean
      // "find the file and describe it again".
      patch(it.id, { status: 'error', error: (e as Error).message });
    }
  }

  async function uploadAll() {
    const queue = items.filter((i) => i.status === 'ready' || i.status === 'error');
    if (!queue.length) return;
    // A small worker pool — each worker pulls the next item until none are left.
    const cursor = { i: 0 };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
      for (;;) {
        const idx = cursor.i;
        cursor.i += 1;
        if (idx >= queue.length) return;
        await uploadOne(queue[idx]);
      }
    }));
    onUploaded();
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    addFiles(Array.from(e.dataTransfer.files ?? []));
  };

  const allDone = summary.total > 0 && summary.done === summary.total;

  return (
    <div className="space-y-3">
      <p className="text-[11.5px] text-gray-500 leading-snug">
        {t('L’assistant ne voit pas l’image : il lit seulement votre description. Décrivez-la bien, sinon il ne saura pas quand la proposer.')}
      </p>

      {/* ── The drop zone ──────────────────────────────────────────────────
          A label, so a click opens the picker and a keyboard reaches it — a
          div with an onClick is invisible to both. */}
      <label
        onDragEnter={(e) => { e.preventDefault(); dragDepth.current += 1; setDragging(true); }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={() => { dragDepth.current -= 1; if (dragDepth.current <= 0) setDragging(false); }}
        onDrop={onDrop}
        className={`relative flex flex-col items-center justify-center gap-1.5 rounded-2xl
          border-2 border-dashed px-5 py-8 text-center cursor-pointer
          transition-[background-color,border-color,transform] duration-200 ease-out
          ${dragging
            ? 'border-transparent bg-amber-50/80 scale-[1.01]'
            : 'border-gray-200 bg-gray-50/60 hover:bg-gray-50 hover:border-gray-300'}`}
        style={dragging ? { borderColor: GOLD } : undefined}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED_TYPES.join(',')}
          className="sr-only"
          onChange={(e) => { addFiles(Array.from(e.target.files ?? [])); e.target.value = ''; }}
        />
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
          stroke={dragging ? GOLD : 'var(--color-adm-faint)'} strokeWidth="1.6" strokeLinecap="round"
          className={`transition-transform duration-200 ${dragging ? '-translate-y-0.5' : ''}`}>
          <path d="M12 16V4M12 4l-4 4M12 4l4 4" />
          <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
        </svg>
        <span className="text-[13px] font-semibold text-gray-800">
          {dragging ? t('Déposez pour ajouter') : t('Glissez vos images ici')}
        </span>
        <span className="text-[11.5px] text-gray-500">
          {t('ou cliquez pour choisir — plusieurs à la fois')}
        </span>
      </label>

      {/* Refused files say so themselves — silence reads as a bug. */}
      {rejected.length > 0 && (
        <div className="rounded-xl bg-amber-50 px-3 py-2 space-y-0.5" role="status">
          {rejected.map((r) => (
            <p key={r.name} className="text-[11.5px] text-amber-900">
              <span className="font-medium">{r.name}</span> — {t(r.reason)}
            </p>
          ))}
        </div>
      )}

      {/* ── One card per file: thumbnail, its own title, its own description ── */}
      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((it, idx) => {
            const usable = isUsableByAssistant(it);
            const weakTitle = isMeaninglessTitle(it.title);
            return (
              <div
                key={it.id}
                className="storiz-pop-in flex gap-3 rounded-xl border border-gray-200 bg-white p-2.5"
                // Stagger: 30ms per card, capped so a 30-file drop does not
                // take a second to finish appearing.
                style={{ animationDelay: `${Math.min(idx, 12) * 30}ms` }}
              >
                {/* A light island: the photo and its upload wash look exactly as in light (night mode never tints a photo). */}
                <div data-adm-media className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                  <img src={it.previewUrl} alt="" className="h-full w-full object-cover" />
                  {it.status === 'uploading' && (
                    <div className="absolute inset-0 grid place-items-center bg-white/70">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"
                        style={{ borderColor: GOLD, borderTopColor: 'transparent' }} />
                    </div>
                  )}
                  {it.status === 'done' && (
                    <div className="absolute inset-0 grid place-items-center bg-emerald-600/85">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff"
                        strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                  )}
                </div>

                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex items-start gap-2">
                    <input
                      aria-label={t('Titre de l’image')}
                      value={it.title}
                      disabled={it.status === 'done' || it.status === 'uploading'}
                      onChange={(e) => patch(it.id, { title: e.target.value })}
                      placeholder={t('Titre, ex. Exemple d’album')}
                      className={`w-full rounded-lg border px-2.5 py-1.5 text-[12.5px] disabled:bg-gray-50
                        ${weakTitle && it.status === 'ready' ? 'border-amber-300' : 'border-gray-200'}`}
                    />
                    {it.status !== 'done' && it.status !== 'uploading' && (
                      <button
                        type="button" onClick={() => remove(it.id)}
                        aria-label={t('Retirer cette image')}
                        className="mt-0.5 shrink-0 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2.2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                      </button>
                    )}
                  </div>

                  <textarea
                    aria-label={t('Description de l’image')}
                    value={it.description}
                    disabled={it.status === 'done' || it.status === 'uploading'}
                    onChange={(e) => patch(it.id, { description: e.target.value })}
                    placeholder={t('Quand faut-il l’envoyer ? ex. Photo d’un album ouvert, couverture Marrakech.')}
                    className={`w-full rounded-lg border px-2.5 py-1.5 text-[12.5px] min-h-[44px] disabled:bg-gray-50
                      ${!usable && it.status === 'ready' ? 'border-amber-300' : 'border-gray-200'}`}
                  />

                  {/* ⚠️ THE WARNING NAMES THE CONSEQUENCE, not the rule. "Champ
                      requis" tells you what the form wants; this tells you what
                      the assistant will do. */}
                  {it.status === 'ready' && !usable && (
                    <p className="text-[11px] text-amber-800">
                      {t('Sans description, l’assistant ne pourra jamais la proposer.')}
                    </p>
                  )}
                  {it.status === 'ready' && usable && weakTitle && (
                    <p className="text-[11px] text-amber-800">
                      {t('Ce titre ne dit rien — donnez-lui un nom reconnaissable.')}
                    </p>
                  )}
                  {it.status === 'error' && (
                    <p className="text-[11px] text-red-600" role="alert">
                      {t('Échec')} : {it.error}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── The one action, and an honest sentence under it ─────────────── */}
      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={uploadAll}
            disabled={summary.busy || allDone}
            className="h-11 rounded-xl px-4 text-[13.5px] font-semibold text-white disabled:opacity-40"
            style={{ background: GOLD }}
          >
            {summary.busy
              ? t('Envoi…')
              : t('Envoyer {n} image(s)', { n: String(summary.ready + summary.failed) })}
          </button>
          {allDone && (
            <button
              type="button"
              onClick={() => { items.forEach((i) => URL.revokeObjectURL(i.previewUrl)); setItems([]); setRejected([]); }}
              className="h-11 rounded-xl border border-gray-300 px-4 text-[13.5px] font-semibold text-gray-700"
            >
              {t('Ajouter d’autres images')}
            </button>
          )}
          <span className={`text-[12px] ${summary.failed ? 'text-red-600 font-medium'
            : summary.undescribed ? 'text-amber-800' : 'text-gray-600'}`}>
            {queueMessage(summary)}
          </span>
        </div>
      )}
    </div>
  );
}
